import React, { useState, useEffect, useRef, useMemo } from 'react';
import { X, Upload } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import type { NewsItem } from '@/data/news';
import { getSupabaseClient } from '@/utils/supabase/client';
import { projectId, publicAnonKey } from '/utils/supabase/info';
import ReactQuill from 'react-quill';

// Lazy load ReactQuill to avoid CSP issues
import('react-quill/dist/quill.snow.css');

interface NewsEditModalProps {
  newsItem: NewsItem | null;
  isOpen: boolean;
  onClose: () => void;
  onUpdate: (news: Partial<NewsItem>) => void;
  onCreate?: (news: Omit<NewsItem, 'id'>) => void;
}

export function NewsEditModal({ newsItem, isOpen, onClose, onUpdate, onCreate }: NewsEditModalProps) {
  const [formData, setFormData] = useState({
    titleHu: '',
    titleEn: '',
    excerptHu: '',
    excerptEn: '',
    contentHu: '',
    contentEn: '',
    image: '',
    date: '',
    author: ''
  });
  
  const [uploading, setUploading] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const quillHuRef = useRef<ReactQuill>(null);
  const quillEnRef = useRef<ReactQuill>(null);
  
  const isCreateMode = !newsItem;

  useEffect(() => {
    console.log('NewsEditModal - newsItem changed:', newsItem);
    
    if (newsItem) {
      console.log('NewsEditModal - Loading news data:', {
        titleHu: newsItem.title.hu,
        contentHu: newsItem.content.hu,
        contentHuLength: newsItem.content.hu?.length
      });
      
      // Try to restore from localStorage if available (in case of accidental data loss)
      const savedDraft = localStorage.getItem(`news-draft-${newsItem.id}`);
      if (savedDraft) {
        const draft = JSON.parse(savedDraft);
        const draftTime = new Date(draft.savedAt).getTime();
        const now = new Date().getTime();
        
        // If draft is less than 1 hour old and has more content than current
        if (now - draftTime < 3600000 && draft.contentHu?.length > newsItem.content.hu?.length) {
          const restore = confirm(
            'Található mentett piszkozat ennél a hírnél, amely több tartalmat tartalmaz. Szeretnéd visszaállítani?\n\n' +
            `Piszkozat: ${draft.contentHu?.length || 0} karakter\n` +
            `Jelenlegi: ${newsItem.content.hu?.length || 0} karakter`
          );
          
          if (restore) {
            setFormData({
              titleHu: draft.titleHu || newsItem.title.hu || '',
              titleEn: draft.titleEn || newsItem.title.en || '',
              excerptHu: draft.excerptHu || newsItem.excerpt.hu || '',
              excerptEn: draft.excerptEn || newsItem.excerpt.en || '',
              contentHu: draft.contentHu || newsItem.content.hu || '',
              contentEn: draft.contentEn || newsItem.content.en || '',
              image: draft.image || newsItem.image || '',
              date: draft.date || newsItem.date || '',
              author: draft.author || newsItem.author || ''
            });
            return;
          }
        }
      }
      
      setFormData({
        titleHu: newsItem.title?.hu || '',
        titleEn: newsItem.title?.en || '',
        excerptHu: newsItem.excerpt?.hu || '',
        excerptEn: newsItem.excerpt?.en || '',
        contentHu: newsItem.content?.hu || '',
        contentEn: newsItem.content?.en || '',
        image: newsItem.image || '',
        date: newsItem.date || '',
        author: newsItem.author || ''
      });
    } else {
      // Reset form for create mode
      const today = new Date().toISOString().split('T')[0];
      setFormData({
        titleHu: '',
        titleEn: '',
        excerptHu: '',
        excerptEn: '',
        contentHu: '',
        contentEn: '',
        image: '',
        date: today,
        author: 'ZSALAKÓ Építőműhely'
      });
    }
  }, [newsItem]);

  // Auto-save draft to localStorage
  useEffect(() => {
    if (!newsItem) return; // Don't save drafts for new items
    
    const saveDraft = () => {
      const draft = {
        ...formData,
        savedAt: new Date().toISOString()
      };
      localStorage.setItem(`news-draft-${newsItem.id}`, JSON.stringify(draft));
    };
    
    // Save every 10 seconds while editing
    const interval = setInterval(saveDraft, 10000);
    
    return () => clearInterval(interval);
  }, [formData, newsItem]);

  // Image upload handler
  const imageHandler = async (isHungarian: boolean) => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();

    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;

      try {
        setUploading(true);
        
        // Use backend upload endpoint instead of direct Supabase client
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);

        const response = await fetch(
          `https://${projectId}.supabase.co/functions/v1/make-server-586f2897/upload/news`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${publicAnonKey}`
            },
            body: formDataUpload
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Upload failed');
        }

        const { url } = await response.json();
        console.log('Content image uploaded successfully:', url);

        // Insert image into editor
        const quillRef = isHungarian ? quillHuRef : quillEnRef;
        const quill = quillRef.current?.getEditor();
        if (quill) {
          const range = quill.getSelection();
          const position = range ? range.index : 0;
          quill.insertEmbed(position, 'image', url);
          quill.setSelection(position + 1);
        }

        setUploading(false);
      } catch (error) {
        console.error('Error uploading image:', error);
        alert('Hiba történt a kép feltöltésekor: ' + (error as Error).message);
        setUploading(false);
      }
    };
  };

  // Cover image upload handler
  const handleCoverImageUpload = async () => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();

    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;

      try {
        setUploadingCover(true);
        
        // Use backend upload endpoint instead of direct Supabase client
        const uploadFormData = new FormData();
        uploadFormData.append('file', file);

        const response = await fetch(
          `https://${projectId}.supabase.co/functions/v1/make-server-586f2897/upload/news`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${publicAnonKey}`
            },
            body: uploadFormData
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Upload failed');
        }

        const { url } = await response.json();
        console.log('Cover image uploaded successfully:', url);

        // Update form data with the new cover image URL
        setFormData(prev => ({ ...prev, image: url }));
        setUploadingCover(false);
      } catch (error) {
        console.error('Error uploading cover image:', error);
        alert('Hiba történt a borítókép feltöltésekor: ' + (error as Error).message);
        setUploadingCover(false);
      }
    };
  };

  // Quill modules configuration
  const modulesHu = useMemo(() => ({
    toolbar: {
      container: [
        [{ 'header': [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
        [{ 'indent': '-1'}, { 'indent': '+1' }],
        ['link', 'image'],
        [{ 'align': [] }],
        ['clean']
      ],
      handlers: {
        image: () => imageHandler(true)
      }
    }
  }), []);

  const modulesEn = useMemo(() => ({
    toolbar: {
      container: [
        [{ 'header': [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
        [{ 'indent': '-1'}, { 'indent': '+1' }],
        ['link', 'image'],
        [{ 'align': [] }],
        ['clean']
      ],
      handlers: {
        image: () => imageHandler(false)
      }
    }
  }), []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate that image is uploaded
    if (!formData.image) {
      alert('Kérlek tölts fel egy borítóképet!');
      return;
    }

    const newsData = {
      title: {
        hu: formData.titleHu,
        en: formData.titleEn
      },
      excerpt: {
        hu: formData.excerptHu,
        en: formData.excerptEn
      },
      content: {
        hu: formData.contentHu,
        en: formData.contentEn
      },
      image: formData.image,
      date: formData.date,
      author: formData.author
    };

    if (isCreateMode && onCreate) {
      onCreate(newsData);
    } else {
      onUpdate(newsData);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold">
            {isCreateMode ? 'Új Hír Hozzáadása' : 'Hír Szerkesztése'}
          </h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {uploading && (
            <div className="bg-blue-50 border border-blue-200 p-4 rounded-md flex items-center gap-2">
              <Upload className="animate-pulse" />
              <span>Kép feltöltése folyamatban...</span>
            </div>
          )}

          {/* Title */}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Cím (Magyar)
              </label>
              <input
                type="text"
                value={formData.titleHu}
                onChange={(e) => setFormData({ ...formData, titleHu: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Title (English)
              </label>
              <input
                type="text"
                value={formData.titleEn}
                onChange={(e) => setFormData({ ...formData, titleEn: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                required
              />
            </div>
          </div>

          {/* Excerpt */}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Kivonat (Magyar)
              </label>
              <textarea
                value={formData.excerptHu}
                onChange={(e) => setFormData({ ...formData, excerptHu: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 h-24"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Excerpt (English)
              </label>
              <textarea
                value={formData.excerptEn}
                onChange={(e) => setFormData({ ...formData, excerptEn: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 h-24"
                required
              />
            </div>
          </div>

          {/* Content with Rich Text Editor */}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tartalom (Magyar)
              </label>
              <div className="border border-gray-300 rounded-md" style={{ height: '300px' }}>
                <ReactQuill
                  key={`hu-${newsItem?.id || 'new'}-${(formData.contentHu || '').substring(0, 10)}`}
                  ref={quillHuRef}
                  theme="snow"
                  value={formData.contentHu}
                  onChange={(value) => setFormData({ ...formData, contentHu: value })}
                  modules={modulesHu}
                  style={{ height: '240px' }}
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Content (English)
              </label>
              <div className="border border-gray-300 rounded-md" style={{ height: '300px' }}>
                <ReactQuill
                  key={`en-${newsItem?.id || 'new'}-${(formData.contentEn || '').substring(0, 10)}`}
                  ref={quillEnRef}
                  theme="snow"
                  value={formData.contentEn}
                  onChange={(value) => setFormData({ ...formData, contentEn: value })}
                  modules={modulesEn}
                  style={{ height: '240px' }}
                />
              </div>
            </div>
          </div>

          {/* Image, Date, Author */}
          <div className="grid md:grid-cols-3 gap-4 mt-20">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Borítókép
              </label>
              <Button
                type="button"
                onClick={handleCoverImageUpload}
                disabled={uploadingCover}
                className="w-full"
              >
                {uploadingCover ? (
                  <>
                    <Upload className="w-4 h-4 mr-2 animate-pulse" />
                    Feltöltés...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Borítókép Feltöltése
                  </>
                )}
              </Button>
              {!formData.image && (
                <p className="text-xs text-gray-500 mt-1">
                  Tölts fel egy borítóképet
                </p>
              )}
              {formData.image && (
                <p className="text-xs text-green-600 mt-1">
                  ✓ Borítókép beállítva
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Dátum
              </label>
              <input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Szerző
              </label>
              <input
                type="text"
                value={formData.author}
                onChange={(e) => setFormData({ ...formData, author: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                required
              />
            </div>
          </div>

          {/* Preview Image */}
          {formData.image && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Borítókép előnézet
              </label>
              <img 
                src={formData.image} 
                alt="Preview" 
                className="w-full max-h-64 object-cover rounded-md"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            </div>
          )}

          {/* Instructions */}
          <div className="bg-blue-50 border border-blue-200 p-4 rounded-md">
            <p className="text-sm text-blue-800">
              <strong>Képek beszúrása:</strong> Kattints a szerkesztő eszköztárában a kép ikonra, majd válassz egy képet a számítógépedről. 
              A kép automatikusan feltöltődik és beillesztésre kerül a szövegbe.
            </p>
            <p className="text-sm text-blue-800 mt-2">
              <strong>Linkek hozzáadása:</strong> Jelöld ki a szöveget, majd kattints a lánc ikonra és add meg az URL-t.
            </p>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <Button type="button" variant="outline" onClick={onClose}>
              Mégse
            </Button>
            <Button type="submit" disabled={uploading}>
              {isCreateMode ? 'Létrehozás' : 'Mentés'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}